import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { domainRouterClient } from '../../api/domainRouterClient';
import { asRecord, normalizeJsonValue } from './graphql';

type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

type SettingsLiveData = {
  orgId: string | null;
  moduleKey: string;
  version: number;
  updatedAt: number | null;
  payload: Record<string, unknown>;
};

type SettingsLiveState = {
  status: AsyncStatus;
  error: string | null;
  data: SettingsLiveData;
};

const DEFAULT_MODULE_KEY = 'staff_settings';

const initialState: SettingsLiveState = {
  status: 'idle',
  error: null,
  data: {
    orgId: null,
    moduleKey: DEFAULT_MODULE_KEY,
    version: 0,
    updatedAt: null,
    payload: {},
  },
};

type FetchSettingsConfigArgs = {
  orgId?: string;
  moduleKey?: string;
};

export const fetchSettingsModuleConfig = createAsyncThunk(
  'settingsLive/fetchModuleConfig',
  async (args: FetchSettingsConfigArgs = {}): Promise<SettingsLiveData> => {
    const moduleKey = String(args.moduleKey ?? DEFAULT_MODULE_KEY).trim() || DEFAULT_MODULE_KEY;
    const queryArgs = args.orgId
      ? { orgId: args.orgId, moduleKey }
      : { moduleKey };

    const response = asRecord(await domainRouterClient.query('getModuleConfig', queryArgs));
    const payload = normalizeJsonValue<Record<string, unknown>>(response.payload, {});

    return {
      orgId: response.orgId ? String(response.orgId) : null,
      moduleKey: response.moduleKey ? String(response.moduleKey) : moduleKey,
      version: Number(response.version ?? 0),
      updatedAt: response.updatedAt == null ? null : Number(response.updatedAt),
      payload: asRecord(payload),
    };
  }
);

const settingsLiveSlice = createSlice({
  name: 'settingsLive',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSettingsModuleConfig.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchSettingsModuleConfig.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.error = null;
        state.data = action.payload;
      })
      .addCase(fetchSettingsModuleConfig.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? 'Failed to load module configuration.';
      });
  },
});

export const settingsLiveReducer = settingsLiveSlice.reducer;

